/**
 * Delivery Settings - Calendar & Holiday Dataverse API
 *
 * CreateCalendarApi / CreateHolidayApi are single Azure Function endpoints
 * that dispatch on HTTP method, same convention as every other middleware
 * endpoint in this app (GET = list, POST = create, PATCH = update with
 * { id, ...fields }, DELETE = { id }).
 *
 * GET /CreateHolidayApi?calendarId=X returns that calendar's holiday rules
 * as a flat list, in Dataverse's own calendarrule field names:
 *   { calendarruleid, name, _calendarid_value, starttime,
 *     effectiveintervalstart, effectiveintervalend, ... }
 */

import { NetworkMethodEnum, Obj } from "@/types";
import { fetcher } from "@/utils/api/dataverse/fetcher";
import { mutator } from "@/utils/api/dataverse/mutator";
import { ICalendar, IHoliday } from "../types";

export const URL = {
  calendar: "/CreateCalendarApi",
  holiday: "/CreateHolidayApi",
};

export interface ICreateCalendarPayload {
  name: string;
  description?: string | null;
}

export interface IUpdateCalendarPayload extends Partial<ICreateCalendarPayload> {
  id: string;
}

export interface ICreateHolidayPayload {
  calendarId: string;
  name: string;
  /** ISO datetime, e.g. 2026-09-13T00:00:00 */
  scheduledStart: string;
  /** ISO datetime, e.g. 2026-09-13T23:59:59 */
  scheduledEnd: string;
}

export interface IUpdateHolidayPayload extends Partial<Omit<ICreateHolidayPayload, "calendarId">> {
  calendarRuleId: string;
  calendarId: string;
}

const normalizeHolidayRule = (rule: Obj, calendarId: string): IHoliday => ({
  id: rule.calendarruleid ?? rule.id,
  calendarId: rule._calendarid_value ?? calendarId,
  name: rule.name,
  scheduledStart: rule.starttime ?? rule.effectiveintervalstart,
  scheduledEnd: rule.effectiveintervalend ?? rule.starttime,
});

const normalizeCalendar = (raw: Obj): ICalendar => {
  const id = raw.calendarid ?? raw.id;
  return {
    id,
    name: raw.name,
    description: raw.description ?? null,
    // Some responses (e.g. the calendar re-returned after creating a holiday)
    // carry the rules nested — pick them up if present, otherwise empty.
    holidays: (raw.calendar_calendar_rules ?? []).map((rule: Obj) => normalizeHolidayRule(rule, id)),
  };
};

/** Get all calendars (list only, used for the picker) */
export const getCalendarsApi = async (): Promise<ICalendar[]> => {
  const res = await fetcher(URL.calendar);
  const list = res?.value ?? (Array.isArray(res) ? res : res ? [res] : []);
  return (Array.isArray(list) ? list : []).map(normalizeCalendar);
};

/** Get the holiday rules for a specific calendar */
export const getHolidaysApi = async (calendarId: string): Promise<IHoliday[]> => {
  const res = await fetcher(`${URL.holiday}?calendarId=${calendarId}`);
  // Response can come back either as a flat list of calendarrule rows, or as
  // the parent calendar re-fetched with its rules nested — handle both.
  const list = res?.calendar_calendar_rules
    ?? res?.value?.[0]?.calendar_calendar_rules
    ?? res?.value
    ?? (Array.isArray(res) ? res : res ? [res] : []);
  return (Array.isArray(list) ? list : []).map((rule: Obj) => normalizeHolidayRule(rule, calendarId));
};

/** Create a calendar */
export const createCalendarApi = async (payload: ICreateCalendarPayload): Promise<ICalendar> => {
  const res = await mutator(NetworkMethodEnum.POST, URL.calendar, payload);
  return normalizeCalendar(res);
};

/** Update a calendar */
export const updateCalendarApi = async (payload: IUpdateCalendarPayload): Promise<ICalendar> => {
  const res = await mutator(NetworkMethodEnum.PATCH, URL.calendar, payload);
  return normalizeCalendar(res);
};

/** Delete a calendar */
export const deleteCalendarApi = async (id: string): Promise<void> => {
  await mutator(NetworkMethodEnum.DELETE, URL.calendar, { id });
};

/** Create a holiday rule under a calendar — response is the parent calendar with rules re-attached */
export const createHolidayApi = async (payload: ICreateHolidayPayload): Promise<ICalendar> => {
  const res = await mutator(NetworkMethodEnum.POST, URL.holiday, payload);
  return normalizeCalendar(res);
};

/** Update a holiday rule */
export const updateHolidayApi = async (payload: IUpdateHolidayPayload): Promise<ICalendar> => {
  const res = await mutator(NetworkMethodEnum.PATCH, URL.holiday, payload);
  return normalizeCalendar(res);
};

/** Delete a holiday rule by its calendarruleid */
export const deleteHolidayApi = async (calendarRuleId: string): Promise<void> => {
  await mutator(NetworkMethodEnum.DELETE, URL.holiday, { calendarRuleId });
};
